import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, TextField, CircularProgress } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import Modal, { ModalHead, ModalBody, ModalFoot } from "../../../../../component/Modal";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import InlineMessage from "../../../../../component/InlineMessage";
import { useNotify } from "../../../../../component/NotificationProvider";
import { submitSelfReview } from "../../../../../services/performance";
import { tokens } from "../../../../../theme";
import { questionsFor, answersById, reviewProgress } from "./selfGoals";

/**
 * The employee's own half of a review.
 *
 * The questions are the cycle's, not this component's. An earlier draft of the
 * design hard-coded three of them; they are configurable per cycle instead,
 * because "what do you want to take on next cycle" is a question an org should
 * be able to change without a deployment — and because a question carried
 * forward keeps its identity, which is what makes "what did you say last time"
 * possible later.
 *
 * Two things this deliberately does not do:
 *
 * It does not touch competency ratings. `submitSelf` replaces the whole `self`
 * assessment, so posting questions alone would blank any scores already given.
 * They are passed back untouched, and the scoring itself stays on the full
 * review page where the scale and the weights live.
 *
 * It does not claim to save as you type. There is no draft storage on the
 * server — submitting is the only write — so the note says so, and closing with
 * unsaved text asks first rather than quietly discarding a written answer.
 */

const Numeral = ({ n, done }) => (
  <Box
    aria-hidden
    sx={{
      width: "1.25rem",
      height: "1.25rem",
      borderRadius: "50%",
      flex: "none",
      boxSizing: "border-box",
      fontSize: "0.6875rem",
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: done ? tokens.successText : "transparent",
      border: done ? "none" : `1.5px solid ${tokens.borderStrong}`,
      color: done ? tokens.card : tokens.primary,
    }}
  >
    {done ? <CheckIcon sx={{ fontSize: "0.8rem" }} /> : n}
  </Box>
);

const SelfReviewModal = ({ open, review, cycle, onClose, onSubmitted }) => {
  const notify = useNotify();
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const questions = useMemo(() => questionsFor(cycle, "self"), [cycle]);
  const saved = useMemo(() => answersById(review?.self), [review]);

  // Reseed whenever the modal opens, so a discarded draft does not survive to
  // the next visit as though it had been kept.
  useEffect(() => {
    if (open) {
      setAnswers(saved);
      setConfirmClose(false);
    }
  }, [open, saved]);

  const progress = useMemo(() => reviewProgress(questions, answers), [questions, answers]);
  const dirty = useMemo(
    () => questions.some((q) => (answers[String(q._id)] || "") !== (saved[String(q._id)] || "")),
    [questions, answers, saved]
  );

  // Already in: the book's rule is that a submitted review cannot be edited,
  // because the manager writes theirs against it.
  const submitted = review?.status && review.status !== "pending";

  const attemptClose = () => {
    if (dirty && !submitted) { setConfirmClose(true); return; }
    onClose?.();
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await submitSelfReview(review._id, {
        answers: questions.map((q) => ({ question: q._id, text: answers[String(q._id)] || "" })),
        // carried through untouched — this form does not own them
        ratings: review?.self?.ratings || [],
        summary: review?.self?.summary,
      });
      if (res?.success === false) throw new Error(res?.message || "Could not submit your review");
      notify.success("Sent to your manager.");
      onSubmitted?.();
      onClose?.();
    } catch (e) {
      notify.error(e?.message || "Could not submit your review");
    } finally {
      setSubmitting(false);
    }
  };

  const managerName = review?.reviewer?.fname
    ? `${review.reviewer.fname} ${review.reviewer.lname || ""}`.trim()
    : "your manager";

  return (
    <Modal open={open} onClose={attemptClose} width="40rem">
      <ModalHead
        title={`Self-review · ${cycle?.name || "this cycle"}`}
        subtitle={
          submitted
            ? `Submitted. ${managerName} writes theirs against it.`
            : `${managerName} cannot see this until you submit it, and you cannot see theirs until then either.`
        }
        onClose={attemptClose}
      />
      <ModalBody>
        {questions.length === 0 ? (
          <InlineMessage tone="info">
            This cycle does not ask any written questions. Your self-assessment is the
            competency scoring on the full review page.
          </InlineMessage>
        ) : confirmClose ? (
          <InlineMessage tone="warning" title="Close without submitting?">
            Your answers are not saved until you submit them, so closing now loses what
            you have written.
          </InlineMessage>
        ) : (
          <Stack spacing={2.5}>
            {questions.map((q, i) => {
              const value = answers[String(q._id)] || "";
              return (
                <Box key={q._id}>
                  <Stack direction="row" alignItems="baseline" spacing={1.25} sx={{ mb: "0.5rem" }}>
                    <Numeral n={i + 1} done={Boolean(value.trim())} />
                    <Box sx={{ flex: 1, fontSize: "0.875rem", fontWeight: 600, color: tokens.text }}>
                      {q.prompt}
                      {q.required === false && (
                        <Box component="span" sx={{ fontWeight: 400, color: tokens.textMuted }}> · optional</Box>
                      )}
                    </Box>
                  </Stack>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    value={value}
                    disabled={submitted}
                    onChange={(e) =>
                      setAnswers((p) => ({ ...p, [String(q._id)]: e.target.value }))
                    }
                    inputProps={{ "aria-label": q.prompt }}
                    sx={{ "& .MuiInputBase-root": { fontSize: "0.875rem", borderRadius: "8px" } }}
                  />
                  {q.hint && (
                    <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, mt: "0.3125rem" }}>{q.hint}</Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </ModalBody>
      <ModalFoot
        note={
          submitted || !questions.length
            ? undefined
            : `${progress.answered} of ${progress.total} answered · not saved until you submit`
        }
      >
        {confirmClose ? (
          <>
            <BtnSecondary onClick={() => setConfirmClose(false)} sx={{ borderRadius: "8px" }}>
              Keep writing
            </BtnSecondary>
            <BtnPrimary onClick={() => onClose?.()} sx={{ borderRadius: "8px" }}>
              Discard
            </BtnPrimary>
          </>
        ) : (
          <>
            <BtnSecondary onClick={attemptClose} sx={{ borderRadius: "8px" }}>
              Close
            </BtnSecondary>
            {!submitted && questions.length > 0 && (
              <BtnPrimary
                onClick={submit}
                disabled={!progress.canSubmit || submitting}
                sx={{ borderRadius: "8px" }}
              >
                {submitting ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : `Send to ${managerName.split(" ")[0]}`}
              </BtnPrimary>
            )}
          </>
        )}
      </ModalFoot>
    </Modal>
  );
};

export default SelfReviewModal;
